import React from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import MiniWaves from '@/components/animations/MiniWaves'
import { useTheme } from '@/context/ThemeContext'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Message } from 'ai'
import {
  Search,
  Loader2,
  FileText,
  ChevronDown,
  User,
  Calendar,
  Kanban,
  ExternalLink,
  Copy as CopyIcon,
  Check,
} from 'lucide-react'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import ThumbsFeedback from '@/components/dashboard/roadmap/opportunity-card/ThumbsFeedback'
import { toast } from 'react-hot-toast'
import { AuthContext } from '@/context/AuthContext'

export type SearchSourceFrontend = {
  footnote_marker: string
  id: string
  type: 'need' | 'feedback' | 'quote' | 'problem' | 'transcript_section' | 'competitor_capability' | 'news_story'
  title?: string
  content: string
  created_at: string
  is_cluster?: boolean
  meetings_count?: number
  who_said_it?: string
  children?: SearchSourceFrontend[]
  meeting_name?: string
  project_name?: string
  original_transcript_segment?: string

  // Embedding vector distance, backend-only
  vector_distance?: number
}

// Add this helper function at the top of the file, before the CitationTooltip component
function formatSourceType(type: string): string {
  return type
    .split(/[_ ]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Add this function before the CitationTooltip component
function getSourceTypeColor(type: string): string {
  const colorMap: Record<string, string> = {
    need: '#FFC921',
    pain_point: '#FF4A8E',
    feedback: '#D1EC26',
    quote: '#34AED7',
    transcript_section: '#40424A',
  }
  return colorMap[type.toLowerCase()] || '#40424A' // default color if type not found
}

// Update the Citation Tooltip component
const CitationTooltip: React.FC<{ source: SearchSourceFrontend; index: string }> = ({ source, index }) => {
  if (!source) {
    return <></>
  }

  const badgeColor = getSourceTypeColor(source.type)
  const textColor = ['#D1EC26', '#FFC921'].includes(badgeColor) ? 'text-gray-900' : 'text-white'

  const handleViewSource = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Navigate to source or open source in a modal
    console.log('View source clicked for:', source.id)
    // Add navigation or modal opening logic here
  }

  // For quotes, display differently
  const isQuote = source.type === 'quote'
  const title = isQuote ? source.content : source.title
  const content = isQuote ? source.original_transcript_segment : source.content

  return (
    <div className="max-w-sm text-gray-100">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <span className="text-xs font-semibold inline-flex items-center justify-center px-1.5 min-w-[20px] h-5 rounded bg-white text-black mr-2">
            {index}
          </span>
          <span
            className={`text-xs px-2 py-0.5 rounded-sm capitalize ${textColor}`}
            style={{ backgroundColor: badgeColor }}
          >
            {formatSourceType(source.type)}
          </span>
        </div>
        <span className="text-xs text-gray-400">{source.created_at}</span>
      </div>

      {title && <h3 className="text-sm font-medium text-gray-100 mb-2">{title}</h3>}

      <p className="text-xs text-gray-300 mb-3 line-clamp-4">{content}</p>

      <div className="flex flex-col gap-1.5 text-xs pt-2 border-t border-gray-700">
        {source.who_said_it && (
          <span className="text-gray-300 font-medium flex items-center gap-1.5">
            <User className="h-3 w-3" />
            {source.who_said_it}
          </span>
        )}
        {source.meeting_name && (
          <span className="text-gray-300 flex items-center gap-1.5">
            <Calendar className="h-3 w-3" />
            {source.meeting_name}
          </span>
        )}
        {source.project_name && (
          <span className="text-gray-300 flex items-center gap-1.5">
            <Kanban className="h-3 w-3" />
            {source.project_name}
          </span>
        )}
      </div>

      {/* Commented out temporarily while we do the Tooltip overhaul */}
      {/* <Button className="w-full mt-4 py-2" onClick={handleViewSource}>
        <ExternalLink className="h-4 w-4 mr-1" />
        View Source
      </Button> */}
    </div>
  )
}

type MessageType = Message

export type ChatMessageProps = {
  message: MessageType
}

const SourcesCollapsible: React.FC<{ data: any; isSubSource?: boolean }> = ({ data, isSubSource }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const { isDarkMode } = useTheme()

  // Debug log for source data
  React.useEffect(() => {
    if (data && data.length > 0) {
      console.log('SourcesCollapsible data:', data)
      console.log('First source item:', data[0])
      console.log('Has footnote_marker?', 'footnote_marker' in data[0])

      // Add specific checks for footnote_marker
      data.forEach((item: any, index: number) => {
        if (index < 3) {
          // Just check first 3 items
          console.log(`Item ${index} properties:`, Object.keys(item))
          console.log(`Item ${index} footnote_marker:`, item.footnote_marker)

          // Check if the item has a footnote marker in the new format [^X]
          if (item.footnote_marker && typeof item.footnote_marker === 'string') {
            const isNewFormat = /\[\^\d+\]/.test(item.footnote_marker)
            console.log(`Item ${index} has new format footnote marker?`, isNewFormat)

            // Check if footnote_marker exists but is not visible
            const markerValue = item.footnote_marker
            console.log(`Item ${index} footnote_marker type:`, typeof markerValue)
            console.log(`Item ${index} footnote_marker length:`, markerValue ? markerValue.length : 'N/A')
            console.log(`Item ${index} footnote_marker value:`, markerValue)
            console.log(
              `Item ${index} footnote_marker char codes:`,
              markerValue && typeof markerValue === 'string'
                ? Array.from(markerValue).map((c) => c.charCodeAt(0))
                : 'N/A'
            )
          }
        }
      })
    }
  }, [data])

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={`w-full rounded-md ${isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50'} mt-2`}
    >
      <div className="flex items-center justify-between px-3 py-1.5">
        <h4 className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          {data.length} {` ${isSubSource ? 'grouped source' : 'source'}${data.length === 1 ? '' : 's'}`}
          {data[0]?.footnote_marker && <span className="ml-2 text-xs text-[#FF4A8E]">(with footnote markers)</span>}
        </h4>
        <CollapsibleTrigger className="rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 p-1 transition-colors">
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isOpen ? 'transform rotate-180' : ''} ${
              isDarkMode ? 'text-gray-300' : 'text-gray-500'
            }`}
          />
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        <div className="max-h-96 overflow-y-auto px-3 py-2">
          {data.map((source: SearchSourceFrontend) => (
            <div
              key={source.id}
              className={`mb-2 p-2 rounded-md ${
                isDarkMode ? 'bg-gray-800/50' : 'bg-white/50'
              } border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}
            >
              <div className="flex justify-between items-start mb-1">
                <div className="flex items-center">
                  {source.footnote_marker && (
                    <span className="text-xs font-semibold inline-flex items-center justify-center w-5 h-5 rounded-full bg-black text-white mr-2 border border-black">
                      {source.footnote_marker.replace(/[\[\]^]/g, '')}
                    </span>
                  )}
                  <span
                    className={`text-xs px-2 py-0.5 rounded-sm capitalize border
                    ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-700 text-gray-300'
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                    }`}
                  >
                    {formatSourceType(source.type)}{' '}
                    <span className="lowercase">
                      {source.is_cluster && source.children && source.children.length > 1
                        ? `(${source.meetings_count} meeting${source.meetings_count === 1 ? '' : 's'})`
                        : ''}
                    </span>
                  </span>
                </div>
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{source.created_at}</span>
              </div>

              {/* Debug info for footnote marker */}
              {source.footnote_marker && (
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                  Raw marker: {source.footnote_marker}
                </div>
              )}

              {source.title && (
                <h5 className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {source.title}
                </h5>
              )}

              {source.type !== 'transcript_section' && (
                <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{source.content}</p>
              )}

              {source.children && source.children.length > 1 && (
                <div className="flex flex-col gap-2">
                  <SourcesCollapsible isSubSource={true} data={source.children} />
                </div>
              )}

              <div className="flex justify-between items-center mt-1">
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {source.who_said_it}
                </span>
                {source.who_said_it && (
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {source.who_said_it}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user'
  const { isDarkMode } = useTheme()
  const { user } = React.useContext(AuthContext)
  const devEmails = React.useMemo(() => import.meta.env.VITE_DEV_EMAILS?.split(',').map((e) => e.trim()) || [], [])
  const isDeveloper = user && devEmails.includes(user.email)

  const [copied, setCopied] = React.useState(false)

  const copyMessageToClipboard = async () => {
    try {
      const textContent = message.parts
        .filter((part) => (part as any).type === 'text')
        .map((part) => (part as any).text)
        .join('\n')

      await navigator.clipboard.writeText(textContent)
      toast.success('Copied to clipboard')

      // Show temporary visual feedback on the icon
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.error('Failed to copy text:', err)
      toast.error('Unable to copy text to clipboard.')
    }
  }

  // Function to parse and render rich text content
  const renderRichText = (content: string, sources: any[]) => {
    const lines = content.split('\n')
    const elements: React.ReactNode[] = []

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex]

      // Detect markdown tables
      const isTableHeader = /^\s*\|.*\|\s*$/.test(line)
      const isSeparator = lineIndex + 1 < lines.length && /^\s*\|[-:| ]+\|\s*$/.test(lines[lineIndex + 1])
      if (isTableHeader && isSeparator) {
        const headers = line
          .split('|')
          .slice(1, -1)
          .map((h) => h.trim())
        const rows: string[][] = []
        lineIndex += 2 // skip header and separator
        while (lineIndex < lines.length && /^\s*\|.*\|\s*$/.test(lines[lineIndex])) {
          const row = lines[lineIndex]
            .split('|')
            .slice(1, -1)
            .map((c) => c.trim())
          rows.push(row)
          lineIndex++
        }
        lineIndex-- // adjust because loop will increment
        elements.push(
          <table key={`table-${lineIndex}`} className="my-4 text-sm border-collapse">
            <thead>
              <tr>
                {headers.map((cell, idx) => (
                  <th key={idx} className="border px-2 py-1 text-left">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx} className="border px-2 py-1">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )
        continue
      }

      // Process headings (# for h1, ## for h2, etc.)
      if (line.startsWith('# ')) {
        elements.push(
          <h1 key={`h1-${lineIndex}`} className="text-xl font-bold mb-3 mt-4">
            {line.substring(2)}
          </h1>
        )
        continue
      }
      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={`h2-${lineIndex}`} className="text-lg font-bold mb-2 mt-3">
            {line.substring(3)}
          </h2>
        )
        continue
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={`h3-${lineIndex}`} className="text-base font-bold mb-2 mt-3">
            {line.substring(4)}
          </h3>
        )
        continue
      } else if (line.startsWith('#### ')) {
        elements.push(
          <h4 key={`h4-${lineIndex}`} className="text-sm font-bold mb-2 mt-3">
            {line.substring(5)}
          </h4>
        )
        continue
      }

      // Process regular paragraphs with bold, italic, badges, and citations
      if (line.trim() !== '') {
        // Replace <Badge> tags with actual Badge components
        const badgeRegex = /<Badge variant="outline">(.+?)<\/Badge>/g

        // Split the line by badge tags to process them separately
        const parts = line.split(badgeRegex)
        const badges =
          line.match(badgeRegex)?.map((match) => {
            const content = match.replace(/<Badge variant="outline">(.+?)<\/Badge>/, '$1')
            return content
          }) || []

        // Combine parts and badges back together with React components
        const renderedParts: React.ReactNode[] = []
        parts.forEach((part, i) => {
          // Process bold and italic in each part
          let processedPart = part
          // Process bold text (**text**)
          const boldParts = processedPart.split(/\*\*(.*?)\*\*/g)
          const processedWithBold: React.ReactNode[] = []

          boldParts.forEach((segment, j) => {
            if (j % 2 === 1) {
              processedWithBold.push(<strong key={`bold-${i}-${j}`}>{segment}</strong>)
            } else if (segment) {
              processedWithBold.push(segment)
            }
          })

          // Process italic text (*text*)
          const processedWithItalic: React.ReactNode[] = []
          processedWithBold.forEach((segment, j) => {
            if (typeof segment === 'string') {
              const italicParts = segment.split(/\*(.*?)\*/g)
              italicParts.forEach((italicSegment, k) => {
                if (k % 2 === 1) {
                  processedWithItalic.push(<em key={`italic-${i}-${j}-${k}`}>{italicSegment}</em>)
                } else if (italicSegment) {
                  // Process citations
                  const citationParts: React.ReactNode[] = []
                  let currentText = ''
                  let inCitation = false
                  let citationBuffer = ''
                  let currentIndex = 0

                  // Handle character by character to account for streaming
                  for (let c = 0; c < italicSegment.length; c++) {
                    const char = italicSegment[c]
                    const nextChar = c + 1 < italicSegment.length ? italicSegment[c + 1] : ''
                    const next2Char = c + 2 < italicSegment.length ? italicSegment[c + 2] : ''

                    // Start of potential citation with the new format [^1]
                    if (char === '[' && nextChar === '^') {
                      if (currentText) {
                        citationParts.push(currentText)
                        currentText = ''
                      }
                      inCitation = true
                      citationBuffer = char
                    }
                    // End of citation
                    else if (char === ']' && inCitation) {
                      citationBuffer += char
                      inCitation = false

                      // Parse the citation with new format [^1]
                      const match = citationBuffer.match(/\[\^(\d+)\]/)
                      if (match) {
                        const [_, index] = match

                        // Find the source with matching footnote_marker, including in children
                        const findSourceByMarker = (sources: any[], marker: string): any => {
                          console.log(`Looking for source with marker: ${marker}`)

                          // First check top level sources
                          const topLevelMatch = sources.find((s) => s.footnote_marker === marker)
                          if (topLevelMatch) {
                            console.log('Found in top level sources:', topLevelMatch)
                            return topLevelMatch
                          }

                          // Then check children of clusters
                          for (const source of sources) {
                            if (source.children) {
                              const childMatch = source.children.find((child: any) => child.footnote_marker === marker)
                              if (childMatch) {
                                console.log('Found in cluster children:', childMatch)
                                return childMatch
                              }
                            }
                          }

                          console.log('No source found for marker:', marker)
                          return null
                        }

                        const source = findSourceByMarker(sources, citationBuffer)

                        citationParts.push(
                          <Tippy
                            theme="custom-dark"
                            key={`tippy-${source?.id || index}-${i}-${j}-${k}-${currentIndex}`}
                            content={<CitationTooltip source={source} index={index} />}
                            interactive={true}
                            appendTo={() => document.body}
                            placement="top"
                            animation="fade"
                            duration={200}
                            delay={[100, 0]}
                            maxWidth={350}
                            trigger="mouseenter"
                            arrow={true}
                          >
                            <span>
                              <Badge
                                key={`citation-${i}-${j}-${k}-${currentIndex}`}
                                variant="outline"
                                className="ml-1 mr-1 cursor-pointer inline-flex items-center justify-center w-5 h-5 p-0 text-xs 
                                  bg-black text-white hover:bg-gray-800 hover:text-white
                                  transition-colors duration-150 rounded-full border-black
                                "
                              >
                                {index}
                              </Badge>
                            </span>
                          </Tippy>
                        )
                        currentIndex++
                      } else {
                        citationParts.push(citationBuffer)
                      }
                      citationBuffer = ''
                    }
                    // Inside citation
                    else if (inCitation) {
                      citationBuffer += char
                    }
                    // Regular text
                    else {
                      currentText += char
                    }
                  }

                  // Add any remaining text and partial citation
                  if (currentText) {
                    citationParts.push(currentText)
                  }
                  if (citationBuffer) {
                    citationParts.push(citationBuffer)
                  }

                  processedWithItalic.push(...citationParts)
                }
              })
            } else {
              processedWithItalic.push(segment)
            }
          })

          renderedParts.push(
            <React.Fragment key={`part-${i}`}>
              {processedWithItalic}
              {badges[i] && (
                <Badge
                  variant="outline"
                  className={`ml-2 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`}
                >
                  {badges[i]}
                </Badge>
              )}
            </React.Fragment>
          )
        })

        elements.push(
          <p key={`p-${lineIndex}`} className={`text-[15px] leading-relaxed ${lineIndex > 0 ? 'mt-3' : ''}`}>
            {renderedParts}
          </p>
        )
        continue
      }

      // Return empty lines as breaks
      elements.push(<br key={`br-${lineIndex}`} />)
    }

    return elements
  }

  return (
    <div className={`flex mb-8 ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex gap-4 ${isUser ? 'max-w-[540px]' : 'w-full flex-row'}`}>
        <div
          className={`${isUser ? 'rounded-lg p-4 ' : ''}font-manrope text-[15px] font-semibold ${
            isUser
              ? isDarkMode
                ? 'bg-black text-white'
                : 'bg-black text-white'
              : isDarkMode
                ? 'text-gray-200'
                : 'text-gray-800'
          } ${isUser ? '' : 'w-full min-h-[48px]'}`}
        >
          {message.parts.map((part) => {
            switch (part.type as any) {
              // render text parts as simple text:
              case 'text':
                const sources = message.parts
                  .filter(
                    (part) =>
                      (part.type === 'tool-invocation' &&
                        part.toolInvocation.toolName === 'search' &&
                        part.toolInvocation.state === 'result') ||
                      (part as any).type === 'tool-result'
                  )
                  .map((part) => (part as any)?.toolInvocation?.result?.data || (part as any)?.result?.data)
                  .flat()
                  .filter(Boolean) // Remove undefined or null sources

                // Log and ensure sources are valid
                if (sources.length > 0) {
                  console.log('Sources for rendering:', sources.length)
                  // Check for valid footnote markers in the current format
                  const sourcesWithMarkers = sources.filter(
                    (source: any) => source && source.footnote_marker && typeof source.footnote_marker === 'string'
                  )
                  console.log('Sources with valid footnote markers:', sourcesWithMarkers.length)
                }

                return renderRichText((part as any).text, sources)
                // for debugging:
                return <div className="whitespace-pre-wrap">{JSON.stringify(part, null, 2)}</div>

              // for tool invocations, distinguish between the tools and the state:
              case 'tool-result':
              case 'tool-invocation': {
                const callId = (part as any)?.toolInvocation?.toolCallId || (part as any).toolCallId
                const toolName = (part as any)?.toolInvocation?.toolName || (part as any).toolName
                const state = (part as any)?.toolInvocation?.state || 'result'

                switch (toolName) {
                  case 'search': {
                    switch (state) {
                      case 'partial-call':
                      case 'call':
                        return (
                          <div key={callId} className="my-4">
                            <div className="flex items-start space-x-2.5 py-1 rounded-md">
                              <div
                                className={`flex-shrink-0 p-1.5 rounded-md ${isDarkMode ? 'bg-gray-700/50' : 'bg-pink-50'}`}
                              >
                                <Loader2
                                  className={`h-4 w-4 animate-spin ${isDarkMode ? 'text-[#FF4A8E]' : 'text-[#FF4A8E]'}`}
                                />
                              </div>
                              <div className="pt-0.5">
                                <div className="font-medium text-[14px] flex items-center">
                                  <Search className="h-3.5 w-3.5 text-gray-500 mr-1.5" />
                                  Searching research notes & conversations...
                                </div>
                                <p className="text-[12px] text-gray-500">
                                  Reviewing needs, pain points, product insights, and more.
                                </p>
                              </div>
                            </div>
                          </div>
                        )
                      case 'result': {
                        // Debug log to see what data is coming back
                        const resultData = (part as any)?.toolInvocation?.result?.data || (part as any).result?.data
                        console.log('Search result data:', resultData)

                        // More detailed logging of the first few items
                        if (resultData && resultData.length > 0) {
                          console.log('First result item:', resultData[0])
                          console.log('First item has footnote_marker?', 'footnote_marker' in resultData[0])
                          console.log('First item footnote_marker value:', resultData[0].footnote_marker)

                          // Add detailed logging for meeting_name and project_name
                          console.log('First item meeting_name:', resultData[0].meeting_name)
                          console.log('First item project_name:', resultData[0].project_name)

                          // Log all items with meeting or project names
                          const itemsWithMeetings = resultData.filter((item: any) => item.meeting_name)
                          const itemsWithProjects = resultData.filter((item: any) => item.project_name)
                          console.log(
                            `Items with meeting names: ${itemsWithMeetings.length} out of ${resultData.length}`
                          )
                          console.log(
                            `Items with project names: ${itemsWithProjects.length} out of ${resultData.length}`
                          )

                          // Check all items for footnote_marker and log new format markers
                          const withMarker = resultData.filter(
                            (item: any) => item.footnote_marker && item.footnote_marker.length > 0
                          )
                          console.log(
                            `Items with non-empty footnote_marker: ${withMarker.length} out of ${resultData.length}`
                          )
                        }

                        return (
                          <div key={callId} className="my-8">
                            <div
                              className={`w-full rounded-lg p-4 border ${
                                isDarkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-start space-x-3 py-2 mb-2">
                                <div
                                  className={`flex-shrink-0 p-2 rounded-md ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'}`}
                                >
                                  <FileText className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                                </div>
                                <div className="pt-1">
                                  <div className="font-medium text-[15px]">Search complete</div>
                                  <div className="text-sm text-gray-500">
                                    {resultData?.length || 0} result{resultData?.length === 1 ? '' : 's'}
                                  </div>
                                </div>
                              </div>
                              {isDeveloper && (
                                <>
                                  <SourcesCollapsible data={resultData} />

                                  {/* Debug component to show raw data */}
                                  <div className="mt-4 p-2 border border-gray-300 rounded bg-gray-50">
                                    <details>
                                      <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                                        Debug Raw Data
                                      </summary>
                                      <pre className="mt-2 text-xs whitespace-pre-wrap overflow-auto max-h-96">
                                        {JSON.stringify(resultData, null, 2)}
                                      </pre>
                                    </details>

                                    <button
                                      onClick={() => {
                                        // Monkey patch fetch to debug search responses
                                        const originalFetch = window.fetch
                                        window.fetch = async function () {
                                          // @ts-ignore
                                          const response = await originalFetch.apply(this, arguments)

                                          // Clone the response to avoid consuming it
                                          const clone = response.clone()

                                          // Only log search-related requests
                                          // @ts-ignore
                                          const url = arguments[0]
                                          if (typeof url === 'string' && url.includes('search')) {
                                            try {
                                              const responseData = await clone.json()
                                              console.log('INTERCEPTED SEARCH API RESPONSE:', responseData)
                                            } catch (error) {
                                              console.error('Error intercepting response:', error)
                                            }
                                          }

                                          return response
                                        }

                                        alert(
                                          'Network monitoring enabled! Check console for future search API responses.'
                                        )
                                      }}
                                      className="mt-2 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                      Enable Response Monitoring
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )
                      }
                    }
                    break
                  }
                }
              }
            }
          })}

          {/* Action icons for assistant messages */}
          {!isUser && (
            <div className="flex gap-2 items-center justify-start mt-4 text-gray-500">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  copyMessageToClipboard()
                }}
                aria-label="Copy response"
                className="p-1 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <CopyIcon className="w-4 h-4" />}
              </button>
              <ThumbsFeedback size="small" onClick={(type) => console.log(`Thumb feedback: ${type}`)} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
